import { useState } from 'react'
import { X, Sparkles, ChevronRight, ListChecks } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface QuestionSuggestion {
  suggestion_id: string
  question_text: string
  why: string
  suggested_segments?: string[]
}

interface PlanTheme {
  theme: string
  questions: QuestionSuggestion[]
}

// Pure normalizers (exported for unit test). The LLM-backed /question-generation
// response can omit suggested_segments / questions, so normalize at the parse
// boundary to guarantee arrays downstream — crash-proofs .length / .map renders.
export function normalizeSuggestion(s: QuestionSuggestion): QuestionSuggestion {
  return { ...s, suggested_segments: s.suggested_segments ?? [] }
}

export function normalizePlanThemes(themes: PlanTheme[]): PlanTheme[] {
  return (themes ?? []).map(t => ({
    ...t,
    questions: (t.questions ?? []).map(normalizeSuggestion),
  }))
}

interface GenerateQuestionModalProps {
  projectId: string
  onSelect: (question: string, segments: string[]) => void
  onClose: () => void
  onBatchRun?: (questions: string[]) => void
}

export function GenerateQuestionModal({ projectId, onSelect, onClose, onBatchRun }: GenerateQuestionModalProps) {
  const [goal, setGoal] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<QuestionSuggestion[]>([])
  const [planThemes, setPlanThemes] = useState<PlanTheme[]>([])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [planMode, setPlanMode] = useState(false)

  const generate = async () => {
    if (!goal.trim()) return
    setLoading(true)
    setError(null)
    setSuggestions([])
    setPlanThemes([])
    try {
      const token = localStorage.getItem('echo_session')
      const requestBody: Record<string, string> = { user_goal: goal }
      if (planMode) {
        requestBody.mode = 'research_plan'
      }
      const res = await fetch(`/api/v1/projects/${projectId}/question-generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(requestBody),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string }
        throw new Error(err.error ?? `HTTP ${res.status}`)
      }
      if (planMode) {
        const data = await res.json() as { session_id: string; plan_themes: PlanTheme[] }
        setPlanThemes(normalizePlanThemes(data.plan_themes ?? []))
        setSuggestions([])
        setSessionId(data.session_id)
      } else {
        const data = await res.json() as { session_id: string; suggestions: QuestionSuggestion[] }
        setSuggestions((data.suggestions ?? []).map(normalizeSuggestion))
        setPlanThemes([])
        setSessionId(data.session_id)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleUse = async (suggestion: QuestionSuggestion) => {
    setSelected(suggestion.suggestion_id)
    // Record which suggestion was used (fire-and-forget)
    if (sessionId) {
      const token = localStorage.getItem('echo_session')
      fetch(`/api/v1/projects/${projectId}/question-generation/${sessionId}/use`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ suggestion_id: suggestion.suggestion_id }),
      }).catch(() => {})
    }
    onSelect(suggestion.question_text, suggestion.suggested_segments ?? [])
  }

  const handleBatchRun = () => {
    const allQuestions = planThemes.flatMap(t => t.questions.map(q => q.question_text))
    onBatchRun?.(allQuestions)
  }

  return (
    <div data-testid="generate-question-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border-gray">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-echo-indigo" />
              <h2 className="text-base font-semibold text-deep-ink">Generate a research question</h2>
            </div>
            {/* Research plan mode toggle */}
            <button
              data-testid="research-plan-mode"
              onClick={() => setPlanMode(v => !v)}
              className={`flex items-center gap-1.5 text-xs transition-colors ${planMode ? 'text-echo-indigo font-medium' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <ListChecks className="w-3.5 h-3.5" strokeWidth={1.75} />
              Research plan (5–8 questions)
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors ml-4"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Goal input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-deep-ink">
              What are you trying to understand?
            </label>
            <p className="text-xs text-slate-500">
              {planMode
                ? 'Describe your research objective. The system will generate a structured plan of 5–8 questions grouped by theme.'
                : 'Describe your goal in 1-2 sentences. The system will use your project\'s data to suggest specific research questions.'}
            </p>
            <textarea
              name="user_goal"
              className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-echo-indigo/30 resize-none"
              rows={3}
              placeholder="e.g. I want to understand which renewal message will reduce churn among our highest-value customers"
              value={goal}
              onChange={e => setGoal(e.target.value)}
              disabled={loading}
            />
            <Button
              variant="primary"
              size="sm"
              className="bg-echo-indigo hover:bg-echo-indigo/90"
              disabled={!goal.trim() || loading}
              onClick={generate}
            >
              {loading ? 'Generating…' : planMode ? 'Generate research plan' : 'Generate questions'}
            </Button>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{error}</p>
          )}

          {/* Regular suggestions */}
          {suggestions.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Suggested questions
              </p>
              {suggestions.map(s => (
                <div
                  key={s.suggestion_id}
                  data-testid="question-suggestion"
                  className="border border-border-gray rounded-xl p-4 space-y-3 hover:border-echo-indigo/40 transition-colors"
                >
                  <p className="text-sm font-medium text-deep-ink leading-snug">
                    "{s.question_text}"
                  </p>
                  <p className="text-xs text-slate-500 leading-relaxed">{s.why}</p>

                  {(s.suggested_segments ?? []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {(s.suggested_segments ?? []).map((seg, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2 py-0.5 rounded bg-echo-indigo/10 text-echo-indigo text-xs font-medium"
                        >
                          {seg}
                        </span>
                      ))}
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="use-suggestion"
                    disabled={selected === s.suggestion_id}
                    onClick={() => handleUse(s)}
                    className="flex items-center gap-1"
                  >
                    {selected === s.suggestion_id ? 'Selected' : 'Use this question'}
                    {selected !== s.suggestion_id && <ChevronRight size={13} />}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Research plan themes */}
          {planThemes.length > 0 && (
            <div className="space-y-5">
              {planThemes.map((theme, tIdx) => (
                <div key={tIdx} className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    {theme.theme}
                  </h3>
                  {(theme.questions ?? []).map(q => (
                    <div
                      key={q.suggestion_id}
                      data-testid="question-suggestion"
                      className="border border-border-gray rounded-xl p-4 space-y-3 hover:border-echo-indigo/40 transition-colors"
                    >
                      <p className="text-sm font-medium text-deep-ink leading-snug">
                        "{q.question_text}"
                      </p>
                      <p className="text-xs text-slate-500 leading-relaxed">{q.why}</p>

                      {(q.suggested_segments ?? []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {(q.suggested_segments ?? []).map((seg, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center px-2 py-0.5 rounded bg-echo-indigo/10 text-echo-indigo text-xs font-medium"
                            >
                              {seg}
                            </span>
                          ))}
                        </div>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="use-suggestion"
                        disabled={selected === q.suggestion_id}
                        onClick={() => handleUse(q)}
                        className="flex items-center gap-1"
                      >
                        {selected === q.suggestion_id ? 'Selected' : 'Use this question'}
                        {selected !== q.suggestion_id && <ChevronRight size={13} />}
                      </Button>
                    </div>
                  ))}
                </div>
              ))}

              {/* Batch-run button — only when onBatchRun is defined */}
              {onBatchRun && (
                <Button
                  variant="primary"
                  size="sm"
                  data-testid="batch-run-plan"
                  onClick={handleBatchRun}
                >
                  Run all as research plan
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
